import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { showError, showSuccess } from "../../../utils/toastUtils";

const INITIAL_DATA = {
  name: "",
  email: "",
  phoneNumber: "",
  designation: "Branch Manager",
  gender: "",
  dateOfBirth: "",
  address: "",
  nationality: "Indian",
  assignedBranch: "",
};

const GENDERS = ["male", "female", "other"];

const EditManagerDrawer = ({ isOpen, onClose, onSuccess, manager }) => {
  const isEditMode = Boolean(manager?._id);

  const [formData, setFormData] = useState(INITIAL_DATA);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBranches, setLoadingBranches] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    if (isEditMode) {
      setFormData({
        name: manager.name || "",
        email: manager.email || "",
        phoneNumber: manager.phoneNumber || "",
        designation: manager.designation || "Branch Manager",
        gender: manager.gender || "",
        dateOfBirth: manager.dateOfBirth
          ? manager.dateOfBirth.slice(0, 10)
          : "",
        address: manager.address || "",
        nationality: manager.nationality || "Indian",
        assignedBranch: manager.assignedBranch?._id || "",
      });
    } else {
      setFormData(INITIAL_DATA);
    }
  }, [isOpen, manager, isEditMode]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchBranches = async () => {
      setLoadingBranches(true);
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/branches/get-all`,
        );
        setBranches(res.data?.data || []);
      } catch {
        showError("Failed to load branches");
      } finally {
        setLoadingBranches(false);
      }
    };

    fetchBranches();
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const validateForm = () => {
    const required = [
      "name",
      "email",
      "phoneNumber",
      "designation",
      "assignedBranch",
    ];

    const missing = required.find((f) => !formData[f]?.trim());

    if (missing) {
      showError(`Please fill ${missing}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm() || loading) return;

    setLoading(true);

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        phoneNumber: formData.phoneNumber.trim(),
        userType: "branch_manager",
        designation: formData.designation,
        assignedBranch: formData.assignedBranch,
        gender: formData.gender || undefined,
        dateOfBirth: formData.dateOfBirth || undefined,
        address: formData.address || undefined,
        nationality: formData.nationality || undefined,
      };

      const url = isEditMode
        ? `${process.env.REACT_APP_API_URL}/admins/update/${manager._id}`
        : `${process.env.REACT_APP_API_URL}/admins/create`;

      const method = isEditMode ? "put" : "post";

      await axios[method](url, payload);

      showSuccess(
        isEditMode
          ? "Manager updated successfully"
          : "Manager created successfully",
      );

      onSuccess?.();
      onClose();
    } catch (err) {
      showError(err?.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 bg-black/50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          <motion.div
            className="fixed top-0 right-0 h-full w-[420px] md:w-[520px] lg:w-[600px] bg-white z-50 shadow-2xl overflow-hidden"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <div className="flex items-center justify-between px-6 py-4 bg-brandAccentColor border-b">
              <h2 className="text-lg font-bold text-primaryTextColor">
                {isEditMode ? "Edit Manager" : "Add Manager"}
              </h2>
              <button onClick={onClose}>
                <X size={26} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="px-6 pt-6 space-y-5 overflow-y-auto h-[calc(100vh-80px)]"
            >
              <Input
                label="Full Name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
              />

              <Input
                label="Email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
              />

              <Input
                label="Phone Number"
                name="phoneNumber"
                required
                value={formData.phoneNumber}
                onChange={handleChange}
              />

              <Input
                label="Designation"
                name="designation"
                value={formData.designation}
                onChange={handleChange}
              />

              <Select
                label="Assigned Branch"
                name="assignedBranch"
                value={formData.assignedBranch}
                onChange={handleChange}
                required
                options={branches.map((b) => ({
                  label: b.branchName,
                  value: b._id,
                }))}
                loading={loadingBranches}
              />

              <Select
                label="Gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                options={GENDERS.map((g) => ({ label: g, value: g }))}
                optional
              />

              <Input
                label="Date of Birth"
                type="date"
                name="dateOfBirth"
                value={formData.dateOfBirth}
                onChange={handleChange}
                optional
              />

              <Textarea
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                optional
              />

              <Input
                label="Nationality"
                name="nationality"
                value={formData.nationality}
                onChange={handleChange}
                optional
              />

              <div className="flex gap-4 pt-4 border-t sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border rounded-lg"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="relative flex-1 px-6 py-3 bg-primaryColor text-white rounded-lg disabled:opacity-70"
                >
                  {loading ? (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="animate-spin" size={20} />
                    </span>
                  ) : isEditMode ? (
                    "Update Manager"
                  ) : (
                    "Create Manager"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const Input = ({ label, optional, ...props }) => (
  <div>
    <label className="text-sm font-bold text-primaryTextColor mb-2 block">
      {label}
    </label>
    <input
      {...props}
      required={!optional}
      className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
    />
  </div>
);

const Textarea = ({ label, ...props }) => (
  <div>
    <label className="text-sm font-bold text-primaryTextColor mb-2 block">
      {label}
    </label>
    <textarea
      {...props}
      rows={3}
      className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
    />
  </div>
);

const Select = ({ label, options = [], loading, optional, ...props }) => (
  <div>
    <label className="text-sm font-bold text-primaryTextColor mb-2 block">
      {label}
    </label>
    <select
      {...props}
      required={!optional}
      className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
    >
      <option value="">{loading ? "Loading..." : "Select"}</option>
      {options.map((o) => (
        <option key={o.value || o} value={o.value || o}>
          {o.label || o}
        </option>
      ))}
    </select>
  </div>
);

export default EditManagerDrawer;

// import axios from "axios";
// import { AnimatePresence, motion } from "framer-motion";
// import { Loader2, X } from "lucide-react";
// import { useEffect, useState } from "react";
// import { showError, showSuccess } from "../../../utils/toastUtils";

// const INITIAL_DATA = {
//   name: "",
//   email: "",
//   phoneNumber: "",
//   password: "",
//   designation: "Branch Manager",
//   gender: "",
//   dateOfBirth: "",
//   address: "",
//   nationality: "Indian",
//   assignedBranch: "",
// };

// const GENDERS = ["male", "female", "other"];

// const EditManagerDrawer = ({ isOpen, onClose, onSuccess, manager }) => {
//   const isEditMode = Boolean(manager?._id);

//   const [formData, setFormData] = useState(INITIAL_DATA);
//   const [branches, setBranches] = useState([]);
//   const [loading, setLoading] = useState(false);
//   const [loadingBranches, setLoadingBranches] = useState(false);

//   useEffect(() => {
//     if (!isOpen) return;

//     if (isEditMode) {
//       setFormData({
//         name: manager.name || "",
//         email: manager.email || "",
//         phoneNumber: manager.phoneNumber || "",
//         password: "",
//         designation: manager.designation || "Branch Manager",
//         gender: manager.gender || "",
//         dateOfBirth: manager.dateOfBirth
//           ? manager.dateOfBirth.slice(0, 10)
//           : "",
//         address: manager.address || "",
//         nationality: manager.nationality || "Indian",
//         assignedBranch: manager.assignedBranch?._id || "",
//       });
//     } else {
//       setFormData(INITIAL_DATA);
//     }
//   }, [isOpen, manager, isEditMode]);

//   useEffect(() => {
//     if (!isOpen) return;

//     const fetchBranches = async () => {
//       setLoadingBranches(true);
//       try {
//         const res = await axios.get(
//           `${process.env.REACT_APP_API_URL}/branches/get-all`,
//         );
//         setBranches(res.data?.data || []);
//       } catch {
//         showError("Failed to load branches");
//       } finally {
//         setLoadingBranches(false);
//       }
//     };

//     fetchBranches();
//   }, [isOpen]);

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((p) => ({ ...p, [name]: value }));
//   };

//   const validateForm = () => {
//     const required = [
//       "name",
//       "email",
//       "phoneNumber",
//       "designation",
//       "assignedBranch",
//     ];

//     if (!isEditMode) required.push("password");

//     const missing = required.find((f) => !formData[f]?.trim());

//     if (missing) {
//       showError(`Please fill ${missing}`);
//       return false;
//     }

//     return true;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     if (!validateForm() || loading) return;

//     setLoading(true);

//     try {
//       const payload = {
//         name: formData.name.trim(),
//         email: formData.email.trim().toLowerCase(),
//         phoneNumber: formData.phoneNumber.trim(),
//         userType: "branch_manager",
//         designation: formData.designation,
//         assignedBranch: formData.assignedBranch,
//         gender: formData.gender || undefined,
//         dateOfBirth: formData.dateOfBirth || undefined,
//         address: formData.address || undefined,
//         nationality: formData.nationality || undefined,
//       };

//       if (formData.password) {
//         payload.password = formData.password;
//       }

//       const url = isEditMode
//         ? `${process.env.REACT_APP_API_URL}/admins/update/${manager._id}`
//         : `${process.env.REACT_APP_API_URL}/admins/create`;

//       const method = isEditMode ? "put" : "post";

//       await axios[method](url, payload);

//       showSuccess(
//         isEditMode
//           ? "Manager updated successfully"
//           : "Manager created successfully",
//       );

//       onSuccess?.();
//       onClose();
//     } catch (err) {
//       showError(err?.response?.data?.message || "Operation failed");
//     } finally {
//       setLoading(false);
//     }
//   };

//   if (!isOpen) return null;

//   return (
//     <AnimatePresence>
//       {isOpen && (
//         <>
//           <motion.div
//             className="fixed inset-0 bg-black/50 z-40"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//           />

//           <motion.div
//             className="fixed top-0 right-0 h-full w-[420px] md:w-[520px] lg:w-[600px] bg-white z-50 shadow-2xl overflow-hidden"
//             initial={{ x: "100%" }}
//             animate={{ x: 0 }}
//             exit={{ x: "100%" }}
//             transition={{ type: "spring", stiffness: 300, damping: 30 }}
//           >
//             <div className="flex items-center justify-between px-6 py-4 bg-brandAccentColor border-b">
//               <h2 className="text-lg font-bold text-primaryTextColor">
//                 {isEditMode ? "Edit Manager" : "Add Manager"}
//               </h2>
//               <button onClick={onClose}>
//                 <X size={26} />
//               </button>
//             </div>

//             <form
//               onSubmit={handleSubmit}
//               className="px-6 pt-6 space-y-5 overflow-y-auto h-[calc(100vh-80px)]"
//             >
//               <Input
//                 label="Full Name"
//                 name="name"
//                 required
//                 value={formData.name}
//                 onChange={handleChange}
//               />
//               <Input
//                 label="Email"
//                 name="email"
//                 type="email"
//                 required
//                 value={formData.email}
//                 onChange={handleChange}
//               />
//               <Input
//                 label="Phone Number"
//                 name="phoneNumber"
//                 required
//                 value={formData.phoneNumber}
//                 onChange={handleChange}
//               />

//               {!isEditMode && (
//                 <Input
//                   label="Password"
//                   type="password"
//                   name="password"
//                   required
//                   value={formData.password}
//                   onChange={handleChange}
//                 />
//               )}

//               <Input
//                 label="Designation"
//                 name="designation"
//                 value={formData.designation}
//                 onChange={handleChange}
//               />

//               <Select
//                 label="Assigned Branch"
//                 name="assignedBranch"
//                 value={formData.assignedBranch}
//                 onChange={handleChange}
//                 required
//                 options={branches.map((b) => ({
//                   label: b.branchName,
//                   value: b._id,
//                 }))}
//                 loading={loadingBranches}
//               />

//               <Select
//                 label="Gender"
//                 name="gender"
//                 value={formData.gender}
//                 onChange={handleChange}
//                 options={GENDERS.map((g) => ({ label: g, value: g }))}
//                 optional
//               />

//               <Input
//                 label="Date of Birth"
//                 type="date"
//                 name="dateOfBirth"
//                 value={formData.dateOfBirth}
//                 onChange={handleChange}
//                 optional
//               />

//               <Textarea
//                 label="Address"
//                 name="address"
//                 value={formData.address}
//                 onChange={handleChange}
//                 optional
//               />
//               <Input
//                 label="Nationality"
//                 name="nationality"
//                 value={formData.nationality}
//                 onChange={handleChange}
//                 optional
//               />

//               <div className="flex gap-4 pt-4 border-t sticky bottom-0 bg-white">
//                 <button
//                   type="button"
//                   onClick={onClose}
//                   className="flex-1 px-6 py-3 border rounded-lg"
//                 >
//                   Cancel
//                 </button>

//                 <button
//                   type="submit"
//                   disabled={loading}
//                   className="relative flex-1 px-6 py-3 bg-primaryColor text-white rounded-lg disabled:opacity-70"
//                 >
//                   {loading ? (
//                     <span className="absolute inset-0 flex items-center justify-center">
//                       <Loader2 className="animate-spin" size={20} />
//                     </span>
//                   ) : isEditMode ? (
//                     "Update Manager"
//                   ) : (
//                     "Create Manager"
//                   )}
//                 </button>
//               </div>
//             </form>
//           </motion.div>
//         </>
//       )}
//     </AnimatePresence>
//   );
// };

// const Input = ({ label, optional, ...props }) => (
//   <div>
//     <label className="text-sm font-bold text-primaryTextColor mb-2 block">
//       {label}
//     </label>
//     <input
//       {...props}
//       required={!optional}
//       className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
//     />
//   </div>
// );

// const Textarea = ({ label, ...props }) => (
//   <div>
//     <label className="text-sm font-bold text-primaryTextColor mb-2 block">
//       {label}
//     </label>
//     <textarea
//       {...props}
//       rows={3}
//       className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
//     />
//   </div>
// );

// const Select = ({ label, options = [], loading, optional, ...props }) => (
//   <div>
//     <label className="text-sm font-bold text-primaryTextColor mb-2 block">
//       {label}
//     </label>
//     <select
//       {...props}
//       required={!optional}
//       className="w-full px-4 py-3 border rounded-lg focus:ring-1 focus:ring-accentTextColor outline-none"
//     >
//       <option value="">{loading ? "Loading..." : "Select"}</option>
//       {options.map((o) => (
//         <option key={o.value || o} value={o.value || o}>
//           {o.label || o}
//         </option>
//       ))}
//     </select>
//   </div>
// );

// export default EditManagerDrawer;
